import { theme } from 'antd';

import { Button, ConfigProvider, Dropdown, MenuProps, Space } from 'antd';

import { observer } from 'mobx-react-lite';
import { SelectEventHandler } from 'rc-menu/lib/interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAgentStatus from '../../hooks/agentStatus/useAgentStatus';
import useAgentStatusOptions from '../../hooks/agentStatus/useAgentStatusOptions';
import useAlertOnPluginError from '../../hooks/useAlertOnPluginError';
import useToggle from '../../hooks/useToggle';
import { agentStatusIdType } from '../../model/types';
import { setAgentCurrentStatus } from '../../services/agentStatus/agentStatusServices';
import { usePluginStore } from '../../store/RootStoreContext';
import { PluginError, PluginIsInitializing } from '../../store/childStore/pluginStore';
import { PRIMARY_COLOR } from '../../styles/theme';
import requestWithMessage from '../../utils/requestWithMessage';
import { getPluginStore } from '../../utils/store/getStore';
import {
    afterCallAgentStatusId,
    afterCallAgentStatusWithChatId,
    hasPluginTypeAnyErrors,
    isAnyPluginAvailable,
    isAnyPluginInitializing,
    isCallPluginRelatedAgentStatusId,
    isChatPluginRelatedAgentStatusId,
    sendAgentStatusChangeEventToPlugins
} from '../../utils/store/pluginStore';
import { PluginDefinition, PluginType } from '../plugins/types';
import getAgentStatusIcon from './getStatusIcon';

const isDisabledByCallPluginsErrors = (callPluginErrors: PluginError['CALL'], agentStatusId: agentStatusIdType) =>
    hasPluginTypeAnyErrors(PluginType.CALL, callPluginErrors) && isCallPluginRelatedAgentStatusId(agentStatusId);

const isDisabledDueToCallPluginsInitializing = (
    callPluginInitializing: PluginIsInitializing['CALL'],
    agentStatusId: agentStatusIdType
) =>
    isAnyPluginInitializing(PluginType.CALL, callPluginInitializing) && isCallPluginRelatedAgentStatusId(agentStatusId);

const isDisabledByChatPluginsErrors = (callPluginErrors: PluginError['CHAT'], agentStatusId: agentStatusIdType) =>
    hasPluginTypeAnyErrors(PluginType.CHAT, callPluginErrors) && isChatPluginRelatedAgentStatusId(agentStatusId);

const isDisabledDueToChatPluginsInitializing = (
    chatPluginInitializing: PluginIsInitializing['CHAT'],
    agentStatusId: agentStatusIdType
) =>
    isAnyPluginInitializing(PluginType.CHAT, chatPluginInitializing) && isChatPluginRelatedAgentStatusId(agentStatusId);

const isMenuDisabled = (
    pluginDefinitions: Record<string, PluginDefinition>,
    agentStatusId: agentStatusIdType,
    errors: { callPluginError: PluginError['CALL']; chatPluginError: PluginError['CHAT'] },
    pluginIsInitializing: {
        callPluginInitializing: PluginIsInitializing['CALL'];
        chatPluginInitializing: PluginIsInitializing['CHAT'];
    }
): boolean =>
    [afterCallAgentStatusId, afterCallAgentStatusWithChatId].includes(agentStatusId) ||
    (isAnyPluginAvailable(pluginDefinitions, 'CALL')
        ? isDisabledByCallPluginsErrors(errors.callPluginError, agentStatusId) ||
          isDisabledDueToCallPluginsInitializing(pluginIsInitializing.callPluginInitializing, agentStatusId)
        : isCallPluginRelatedAgentStatusId(agentStatusId)) ||
    (isAnyPluginAvailable(pluginDefinitions, 'CHAT')
        ? isDisabledByChatPluginsErrors(errors.chatPluginError, agentStatusId) ||
          isDisabledDueToChatPluginsInitializing(pluginIsInitializing.chatPluginInitializing, agentStatusId)
        : isChatPluginRelatedAgentStatusId(agentStatusId));

const agentStatusi18nByIdMap = {
    1: 'agentStatus.1',
    2: 'agentStatus.2',
    3: 'agentStatus.3',
    4: 'agentStatus.4',
    5: 'agentStatus.5',
    6: 'agentStatus.6',
    7: 'agentStatus.7',
    8: 'agentStatus.8',
    9: 'agentStatus.9',
    10: 'agentStatus.10',
    11: 'agentStatus.11',
    12: 'agentStatus.12',
    13: 'agentStatus.13'
} as const;

const { colorBorder, colorError, colorSuccess, colorWarning } = theme.defaultAlgorithm(theme.defaultSeed);

const statusButtonColorTypes = {
    1: colorError,
    2: colorSuccess,
    3: colorSuccess,
    4: colorSuccess,
    5: PRIMARY_COLOR,
    6: colorSuccess,
    7: colorWarning,
    8: colorWarning,
    9: colorSuccess,
    10: colorSuccess,
    11: colorSuccess,
    12: colorSuccess,
    13: colorSuccess,
    undefined: colorBorder
} as const;

function AgentStatusDropdown() {
    const { t } = useTranslation('common');
    const [dropdownVisible, toggle, , close] = useToggle(false);

    const {
        currentAgentStatus,
        changingAgentStatusIdTo,
        changeAgentStatusOverMobXStore,
        resetChangingAgentStatusProcess,
        setCurrentAgentStatusOnStore,
        pluginDefinitions,
        pluginsErrors: { CALL: callPluginError, CHAT: chatPluginError },
        pluginIsInitializing: { CALL: callPluginInitializing, CHAT: chatPluginInitializing }
    } = usePluginStore();

    const currentAgentStatusId = currentAgentStatus?.id;

    // hook to display alert if any plugin error
    useAlertOnPluginError({
        callPluginError,
        chatPluginError,
        currentAgentStatusId
    });

    const { data: agentStatusOptions, isLoading: isLoadingStatusOptions } = useAgentStatusOptions();
    const { data: currentStatus, isLoading: isLoadingCurrentStatus, mutate } = useAgentStatus();

    const isLoading = isLoadingStatusOptions || isLoadingCurrentStatus;

    const [selectedKeys, setSelectedKeys] = useState([currentAgentStatusId?.toString()]);

    const handleOnSelect: SelectEventHandler = ({ selectedKeys }) => {
        const statusId = +selectedKeys[0] as agentStatusIdType;

        if (currentAgentStatus.id === statusId) {
            // console.debug('Agent status is already set to this value');
            return;
        }
        const selectedAgentStatus = agentStatusOptions.find((status) => status.id === statusId);

        if (isAnyPluginAvailable(pluginDefinitions)) {
            const shouldChangeAgentStatusWithApi = sendAgentStatusChangeEventToPlugins(selectedAgentStatus);

            if (!shouldChangeAgentStatusWithApi) return;
        }

        changeAgentStatusOverMobXStore(statusId);
        close();
    };

    useEffect(() => {
        if (changingAgentStatusIdTo) {
            try {
                const changeAgentStatus = async (statusId: agentStatusIdType) => {
                    const startMessage = t('message.onAgentStatusChanging', {
                        status: t(agentStatusi18nByIdMap[changingAgentStatusIdTo])
                    });
                    const finishMessage = t('message.onAgentStatusChanged', {
                        status: t(agentStatusi18nByIdMap[changingAgentStatusIdTo])
                    });
                    await requestWithMessage({
                        key: 'change-agent-status',
                        startMessage,
                        finishMessage,
                        apiRequest: async () => {
                            await setAgentCurrentStatus(statusId);
                            await mutate();

                            resetChangingAgentStatusProcess();
                        }
                    });
                };
                changeAgentStatus(changingAgentStatusIdTo);
            } catch (_error) {
                return;
            }
        }
    }, [changingAgentStatusIdTo, mutate, resetChangingAgentStatusProcess, t]);

    useEffect(() => {
        // save currentStatus into store
        if (!currentStatus) return;

        setSelectedKeys((prevState) =>
            prevState?.[0] !== currentStatus.status.id.toString() ? [currentStatus.status.id.toString()] : prevState
        );

        // if (pluginType !== PluginType.CALL) return; // there is no call plugin

        currentStatus?.status?.id !== getPluginStore()?.currentAgentStatus?.id &&
            setCurrentAgentStatusOnStore(currentStatus.status);
        // setAgentStatusId(currentStatus.status.id);
        // setIsAboutToChangeId(null);
        // setChangingAgentStatus(false);
    }, [currentStatus, setCurrentAgentStatusOnStore]);

    const items: MenuProps['items'] = (agentStatusOptions ?? []).map((statusOption) => {
        const isStatusOptionDisabled = isMenuDisabled(
            pluginDefinitions,
            statusOption.id,
            { callPluginError, chatPluginError },
            { callPluginInitializing, chatPluginInitializing }
        );

        return {
            key: statusOption.id.toString(),
            label: (
                <Space className="full-width">
                    {getAgentStatusIcon(statusOption.id)}
                    {t(agentStatusi18nByIdMap[statusOption.id] as any)}
                </Space>
            ),
            disabled: isStatusOptionDisabled
        };
    });

    const statusColor = statusButtonColorTypes[currentStatus?.status?.id];

    return (
        <Dropdown
            menu={{ selectedKeys, onSelect: handleOnSelect, selectable: true, items }}
            onOpenChange={toggle}
            open={dropdownVisible}
            placement="bottom"
            trigger={['click']}
        >
            <ConfigProvider theme={{ token: { colorPrimary: statusColor } }}>
                <Button
                    type="primary"
                    style={{ minWidth: 140 }}
                    loading={isLoading || !!changingAgentStatusIdTo}
                    icon={!(isLoading || !!changingAgentStatusIdTo) && getAgentStatusIcon(currentStatus?.status?.id)}
                    onClick={toggle}
                >
                    {isLoading
                        ? t('agentStatusButtonText.initializing')
                        : changingAgentStatusIdTo
                        ? t('agentStatusButtonText.checkStatus')
                        : t(agentStatusi18nByIdMap[currentStatus?.status?.id] as any)}
                </Button>
            </ConfigProvider>
        </Dropdown>
    );
}

export default observer(AgentStatusDropdown);
